import React from 'react';
import { DollarSign, TrendingUp } from 'lucide-react';

const RevenueStreamPanel = ({ revenueStream }) => {
  const platform = revenueStream?.platform || {};
  const daily = revenueStream?.daily_revenue || [];
  const topVendors = revenueStream?.top_vendors || [];
  const maxRevenue = Math.max(...daily.map((d) => d.revenue), 1);
  const hasData =
    (platform.revenue ?? 0) > 0 ||
    (platform.payments ?? 0) > 0 ||
    (platform.bookings_created ?? 0) > 0;

  return (
    <div className="bg-surface border rounded-lg p-3 sm:p-4">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            Revenue stream (outbox rollups)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Pre-aggregated from{' '}
            <code className="text-xs bg-gray-100 px-1 rounded">analytics.events</code>
            {' '}facts — faster than live SQL for trends.
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Stream revenue</p>
          <p className="text-2xl font-bold text-gray-900 tabular-nums">
            ${(platform.revenue ?? 0).toFixed(2)}
          </p>
        </div>
      </div>

      {!hasData ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No revenue rollups for this period. Complete a booking payment, then run{' '}
          <code className="text-xs bg-gray-100 px-1 rounded">php artisan outbox:publish</code>.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Payments</span>
              <span className="font-semibold tabular-nums">{platform.payments ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Bookings created</span>
              <span className="font-semibold tabular-nums">{platform.bookings_created ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Bookings confirmed</span>
              <span className="font-semibold tabular-nums">{platform.bookings_confirmed ?? 0}</span>
            </div>
          </div>

          {daily.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily revenue</h3>
              <div className="space-y-2">
                {daily.map((day) => (
                  <div key={day.date} className="flex items-center gap-3 text-sm">
                    <span className="w-24 shrink-0 text-gray-500 tabular-nums">{day.date}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full"
                        style={{ width: `${Math.max((day.revenue / maxRevenue) * 100, 4)}%` }}
                      />
                    </div>
                    <span className="w-16 text-right font-medium tabular-nums">
                      ${day.revenue.toFixed(0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {topVendors.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Top vendors (stream)</h3>
              <div className="space-y-2">
                {topVendors.map((vendor) => (
                  <div key={vendor.vendor_profile_id} className="flex justify-between text-sm gap-2">
                    <span className="text-gray-600 truncate">{vendor.business_name}</span>
                    <span className="font-semibold tabular-nums shrink-0">
                      ${vendor.revenue.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RevenueStreamPanel;
